import { ColumnTypes } from '../../../../src/components/table/columnTypes';
import { createSelector } from '@opengov/compose-app/reselect';
import { BasicColumnTypes, IOgqlJobResult, IOgqlV1JobResult } from '@opengov/ogql-types';
import {
  EConditionalComparisonOperators,
  EReportColumnTypes,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';

// tslint:disable-next-line:max-line-length
import { selectAgGridColumnTypes } from '../../../reportExplorer/ReportDataTableAgGrid/selectors/selectAgGridColumnTypes';
import { selectDetailTableColumns } from '../../../reportExplorer/selectors/selectReportExplorerState';
import { getDateFormatFromColumn } from '../../basicCalculation/getDateFormatFromColumn';
import { getTableColumnTypeFromReportColumnType } from '../../helpers/semanticTypeToColumnType';
import { reportLocaleText } from '../../locale';
import {
  indicesOfHiddenColumns,
  removeHiddenData
} from '../../reportConfiguration/effects/reportQueryEffectResponders';
import { IConditionalPreviewTable } from '../components/ConditionalPreviewTable';
import {
  columnTypeCalculables,
  getOptionsForOptionalSecondValueToCompare,
  getOptionsForValuesToCompare
} from '../conditionalUtils';
import { conditionalSelector } from '../state/conditionalState';
import { ConditionalColumnTypeSecondaryLabels, EAllowedConditionalTypes } from '../types';

export const buildValueOptionsFromColumns = (
  columns: IReportColumn[],
  allowedColumnTypes: string[] = (Object as any).values(EAllowedConditionalTypes)
) => {
  const optionCategories = columns
    .filter((c) => (Object as any).values(EAllowedConditionalTypes).includes(c.type) && c.hidden !== true)
    .reduce((categories, column: IReportColumn) => {
      let category = column.type;
      if (column.type === BasicColumnTypes.Year) {
        category = BasicColumnTypes.Date;
      }
      if (column.type === BasicColumnTypes.YearInterval || column.type === BasicColumnTypes.DayInterval) {
        category = BasicColumnTypes.Numeric;
      }
      const selectOption = {
        label: column.displayName,
        value: column.name,
        disabled: !allowedColumnTypes.includes(category),
        secondaryLabel:
          category === BasicColumnTypes.Date
            ? ConditionalColumnTypeSecondaryLabels[getDateFormatFromColumn(column)]
            : ConditionalColumnTypeSecondaryLabels[category]
      };
      if (categories[category]) {
        categories[category].items.push(selectOption);
      } else {
        categories[category] = { disabled: !allowedColumnTypes.includes(category), items: [selectOption] };
      }
      return categories;
    }, {});

  Object.keys(optionCategories).forEach(
    (cat) =>
      optionCategories[cat].items &&
      (optionCategories[cat].items = optionCategories[cat].items.sort((a, b) => (b.label > a.label ? -1 : 1)))
  );
  return optionCategories;
};

export const selectConditional = createSelector(conditionalSelector, (state) => state.conditional);

export const selectIfColumnOptions = createSelector(selectDetailTableColumns, (columns) =>
  buildValueOptionsFromColumns(columns, (Object as any).values(EAllowedConditionalTypes))
);

export const selectCurrentConditionSetup = createSelector(conditionalSelector, (state) => state.currentConditionSetup);

export const selectComparisonValueCustomOptions = createSelector(selectConditional, (conditional) => {
  return [
    {
      label: columnTypeCalculables[conditional.sourceColumn.type].custom.label,
      value: columnTypeCalculables[conditional.sourceColumn.type].custom.type
    }
  ];
});

export const selectValueToCompareOptions = createSelector(
  selectComparisonValueCustomOptions,
  selectDetailTableColumns,
  selectConditional,
  (customOptions, columns, conditional) => ({
    ...buildValueOptionsFromColumns(
      columns,
      (Object as any).values(getOptionsForValuesToCompare(conditional.sourceColumn.type))
    ),
    custom: { disabled: false, items: customOptions }
  })
);

export const selectOptionalSecondValueToCompareOptions = createSelector(
  selectComparisonValueCustomOptions,
  selectDetailTableColumns,
  selectConditional,
  (customOptions, columns, conditional) => ({
    ...buildValueOptionsFromColumns(
      columns,
      (Object as any).values(getOptionsForOptionalSecondValueToCompare(conditional.sourceColumn.type))
    ),
    custom: { disabled: false, items: customOptions }
  })
);

export const selectQueryResult = createSelector(conditionalSelector, (state) => state.queryResult);

const selectColumnDefs = createSelector(selectQueryResult, selectConditional, (result, conditionalColumn) => {
  let columns = [];

  if (result) {
    const { jobResult } = result;
    const queryColumns =
      (jobResult as IOgqlJobResult['jobResult']).columns ||
      (jobResult as IOgqlV1JobResult['jobResult']).metadata.columns;

    columns =
      conditionalColumn.columnType === EReportColumnTypes.conditional
        ? [
            conditionalColumn.sourceColumn,
            conditionalColumn.displayName ? conditionalColumn : conditionalColumn.set('displayName', 'New Column')
          ]
        : [conditionalColumn];

    columns = columns.map((column, i) => {
      const columnType = getTableColumnTypeFromReportColumnType(column);
      return {
        headerName: column.displayName,
        field: queryColumns[i].name,
        type: columnType,
        // Determines whether or not the column header and cells are right aligned
        isValueColumn: columnType !== ColumnTypes.Text ? true : false,
        highlight: i === 1
      };
    });
  }

  return columns;
});

const selectRowData = createSelector(selectQueryResult, (result) => {
  let rowData = [];

  if (result) {
    const { jobResult } = result;
    const { data } = jobResult;
    const queryColumns =
      (jobResult as IOgqlJobResult['jobResult']).columns ||
      (jobResult as IOgqlV1JobResult['jobResult']).metadata.columns;
    const removableIndices = indicesOfHiddenColumns(queryColumns);
    const rawRowData = removeHiddenData(data, removableIndices);
    const visibleColumns = (queryColumns as IOgqlJobResult['jobResult']['columns']).filter(
      (column, ndx) => !removableIndices.includes(ndx)
    );

    rowData = rawRowData
      .map((row) => {
        // when no data comes back the first row is an empty array so we need to filter it out
        if (!row.length) {
          return null;
        }
        return row.reduce((acc, value, index) => {
          acc[visibleColumns[index].name] = value;
          return acc;
        }, {});
      })
      .filter((row) => !!row);
  }

  return rowData;
});

export const selectConditionalPreviewTableProps = createSelector(
  conditionalSelector,
  reportLocaleText,
  selectColumnDefs,
  selectRowData,
  selectAgGridColumnTypes,
  (state, reportMessages, columnDefs, rowData, columnTypes): IConditionalPreviewTable => ({
    conditionalPreviewTableProps: {
      rowData,
      columnDefs,
      gridContainerStyle: { height: '200px' },
      onGridReady: ({ api }) => {
        api.sizeColumnsToFit();
      },
      gridOptions: { columnTypes }
    },
    conditionalPreviewTableStatus: state.conditionalPreviewTableStatus,
    reportMessages
  })
);

export const selectConditionalOperatorOptions = createSelector(reportLocaleText, (localeText) => {
  const {
    calculations: {
      conditionalModal: {
        greaterThanOperator,
        greaterThanOrEqualToOperator,
        lessThanOperator,
        lessThanOrEqualToOperator,
        equalToOperator,
        notEqualToOperator,
        betweenOperator
      }
    }
  } = localeText;
  const operationOptions = {
    [EConditionalComparisonOperators.greaterThan]: {
      label: greaterThanOperator,
      value: EConditionalComparisonOperators.greaterThan
    },
    [EConditionalComparisonOperators.greaterThanOrEqualTo]: {
      label: greaterThanOrEqualToOperator,
      value: EConditionalComparisonOperators.greaterThanOrEqualTo
    },
    [EConditionalComparisonOperators.lessThan]: {
      label: lessThanOperator,
      value: EConditionalComparisonOperators.lessThan
    },
    [EConditionalComparisonOperators.lessThanOrEqualTo]: {
      label: lessThanOrEqualToOperator,
      value: EConditionalComparisonOperators.lessThanOrEqualTo
    },
    [EConditionalComparisonOperators.equalTo]: {
      label: equalToOperator,
      value: EConditionalComparisonOperators.equalTo
    },
    [EConditionalComparisonOperators.notEqualTo]: {
      label: notEqualToOperator,
      value: EConditionalComparisonOperators.notEqualTo
    },
    [EConditionalComparisonOperators.between]: {
      label: betweenOperator,
      value: EConditionalComparisonOperators.between
    }
  };
  return Object.values(operationOptions);
});
